import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { GearSix as GearSixIcon } from '@phosphor-icons/react';
import { SignOut as SignOutIcon } from '@phosphor-icons/react';
import { User as UserIcon } from '@phosphor-icons/react';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useAuthHeader } from '../../../hooks/useAuthHeader';
import { useUserContext } from '../../../hooks/useUserContext';
import { useNavItems } from '../../../hooks/useNavItems';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { paths } from '../../../utils/paths';


export interface UserPopoverProps {
  anchorEl: Element | null;
  onClose: () => void;
  open: boolean;
}

export function UserPopover({ anchorEl, onClose, open }: UserPopoverProps): React.JSX.Element {
  const navigate = useNavigate();
  const {dispatch} = useAuthContext();
  const authHeader = useAuthHeader();
  const useNav = useNavItems();
  const [popPaths, setpopPaths] = useState({settings: null, account: null});
  const user = useUserContext();
  
  useEffect(()=> {
    const navs = useNav.getPopOverPaths();
    if(navs.settings === null || navs.account === null){
      Swal.fire({title: 'Not Authorized!', text: 'You are not authorized to view this page, redirecting to Login!', icon: 'error', confirmButtonText: 'Ok' }).then((result) => {
        dispatch({type: 'LOGOUT', payload: null})
        navigate(paths.auth.signIn, {replace: true});
      });
    }else{
      setpopPaths(navs);
    }
  },[]);
  
  const handleSignOut = useCallback(async (): Promise<void> => {
    try {
        dispatch({type: 'LOGOUT', payload: null})
        navigate(paths.auth.signIn, {replace: true});
    } catch (err) {
        authHeader.removeAuthHeader(); 
        navigate(paths.auth.signIn, {replace: true});
    }
  }, [navigate]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { width: '240px' } }}
    >
      <Box sx={{ p: '16px 20px ' }}>
        <Typography variant="subtitle1">{user.lastName} {user.firstName}</Typography>
        <Typography color="text.secondary" variant="body2">
          {user.email}
        </Typography>
      </Box>
      <Divider />
      <MenuList disablePadding sx={{ p: '8px', '& .MuiMenuItem-root': { borderRadius: 1 } }}>
        <MenuItem component={Link} to={popPaths.settings} onClick={onClose}>
          <ListItemIcon>
            <GearSixIcon fontSize="var(--icon-fontSize-md)" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem component={Link} to={popPaths.account} onClick={onClose}>
          <ListItemIcon>
            <UserIcon fontSize="var(--icon-fontSize-md)" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <SignOutIcon fontSize="var(--icon-fontSize-md)" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </MenuList>
    </Popover>
  );
}
